import { z } from 'zod';
/** Director: create a new invitation. Email + opposition + (optional) prospect to convert. */
export const createStudentInvitationSchema = z.object({
    email: z.string().email().toLowerCase(),
    opposition_id: z.string().uuid(),
    prospect_id: z.string().uuid().optional(),
    /**
     * Fecha de inicio (clases y cobro) que fija el DIRECTOR al invitar; el alumno
     * la hereda al aceptar y nunca puede enviarla (acceptStudentInvitationSchema
     * es .strict() y no la incluye).
     */
    start_date: z.string().date('Fecha inválida').optional(),
});
/** Maximum number of invitations the director can send in a single batch. */
export const BULK_INVITATION_MAX = 200;
/**
 * Director: invite many students to the same opposition at once.
 * Emails are lower-cased and de-duplicated; partial success is the norm
 * (some emails may already exist or already have a pending invitation) so the
 * endpoint reports a per-email result rather than failing the whole batch.
 */
export const bulkCreateStudentInvitationSchema = z.object({
    opposition_id: z.string().uuid(),
    emails: z
        .array(z.string().trim().toLowerCase().email('Email inválido'))
        .min(1, 'Añade al menos un email')
        .max(BULK_INVITATION_MAX, `Máximo ${BULK_INVITATION_MAX} invitaciones por lote`)
        // De-duplicate while preserving order so the director isn't billed twice
        // for a repeated address pasted into the list.
        .transform((arr) => Array.from(new Set(arr))),
});
/**
 * Public: payload sent by the student when accepting an invitation.
 * Mirrors the field set the director would otherwise complete in the manual
 * "create student" form, with a couple of additions specific to the
 * self-onboarding flow:
 *   - `shift_id` chosen by the student (must belong to the invited opposition)
 *   - `password` chosen by the student (replaces the temp password flow)
 *   - `privacy_consent` checkbox + LOPD timestamp/IP captured server-side
 *   - `avatar_path` for the photo uploaded to Storage during onboarding
 */
export const acceptStudentInvitationSchema = z
    .object({
    /**
     * Required. The academy assigns the student a number ahead of time (printed
     * on a card, dictated by phone, etc.) and the student must enter it here.
     * Uniqueness against active/temporary-leave students is validated server-side.
     * `preprocess` maps an empty field to `undefined` so the message is the
     * "required" one rather than a coercion artifact.
     */
    student_number: z.preprocess((v) => (v === '' || v === null || v === undefined ? undefined : Number(v)), z
        .number({
        required_error: 'El número de alumno es obligatorio',
        invalid_type_error: 'El número de alumno es obligatorio',
    })
        .int('Número de alumno inválido')
        .positive('Número de alumno inválido')
        .max(99999, 'Número de alumno inválido')),
    first_name: z.string().trim().min(2).max(100),
    last_name: z.string().trim().min(2).max(200),
    dni: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^\d{8}[A-Z]$/, 'DNI inválido (formato: 12345678A)'),
    phone: z
        .string()
        .trim()
        .regex(/^[6789]\d{8}$/, 'Teléfono inválido'),
    address: z.string().trim().min(5).max(300),
    birth_date: z.string().date('Fecha inválida'),
    shift_id: z.string().uuid(),
    /**
     * Profile photo as a data URL (`data:image/<jpeg|png|webp>;base64,...`).
     * Capped at ~7.5 MB raw which decodes to roughly 5.5 MB binary — generous
     * for a modern phone selfie but well below the auth payload ceiling.
     */
    avatar_data_url: z
        .string()
        .regex(/^data:image\/(jpeg|jpg|png|webp);base64,[A-Za-z0-9+/=]+$/, 'Foto inválida')
        .max(7_500_000, 'La foto pesa demasiado'),
    password: z
        .string()
        .min(8, 'Mínimo 8 caracteres')
        .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
        .regex(/\d/, 'Debe contener al menos un número'),
    privacy_consent: z
        .literal(true, { errorMap: () => ({ message: 'Debes aceptar el tratamiento de datos' }) }),
})
    .strict();
//# sourceMappingURL=student-invitation.schema.js.map